// Control-pane styling for the dependency-free Pane (src/ui/controls/pane).
// Supplies what the removed control-library bundle used to inject: the short
// CSS vars the pane DOM consumes (mapped from magviz theme vars) and the base
// structure for the `.mz-*` classes the pane emits. Theme COLORS still come
// from the `!important` overrides in css/style.css (scoped to
// #controls-container); this file supplies the layout.
//
// Injected once into <head>; the cascade tracks live theme changes because
// every value resolves through `var(--magviz-*)` set on :root.
//
// Spacing/size vars (4 px gaps, 20 px unit, 160 px value column) reproduce the
// gaps the pre-removal build rendered with — those knobs were never overridden,
// so the library defaults were what actually showed.

const PANE_STYLE_ID = 'magviz-control-pane-theme';

export function applyControlPaneStyles(theme: { ui?: unknown } | null | undefined): void {
    void theme; // values resolve from :root via magviz CSS vars; arg is legacy
    let style = document.getElementById(PANE_STYLE_ID);
    if (!style) {
        style = document.createElement('style');
        style.id = PANE_STYLE_ID;
        document.head.appendChild(style);
    }
    if (style.textContent) return; // idempotent; the cascade tracks theme changes
    style.textContent = PANE_CSS;
}

const PANE_CSS = `
#controls-container .mz-rotv {
    --bld-br: 4px;
    --bld-vw: 160px;
    --cnt-usz: 20px;
    --cnt-hp: 4px;
    --cnt-vp: 4px;
    --cnt-usp: 4px;
    --in-bg: var(--magviz-input-bg);
    --in-bg-h: var(--magviz-input-bg);
    --in-fg: var(--magviz-text);
    --btn-bg: var(--magviz-button-bg);
    --btn-bg-h: var(--magviz-button-bg-hover);
    --btn-fg: var(--magviz-button-text);
    --cnt-bg: var(--magviz-panel-header-bg);
    --cnt-fg: var(--magviz-text-muted);
    --lbl-fg: var(--magviz-text-muted);
    --grv-fg: var(--magviz-panel-border);
    font: 500 11px/1.3 'Roboto Mono', 'Source Code Pro', Menlo, Courier, monospace;
    text-align: left;
}
/* Containers render statically expanded (no collapse animation) — natural height. */
#controls-container .mz-rotv_c { box-sizing: border-box; position: relative; overflow: visible; padding-top: var(--cnt-vp); padding-bottom: var(--cnt-vp); }
#controls-container .mz-fldv { position: relative; }
#controls-container .mz-fldv_c { box-sizing: border-box; position: relative; overflow: visible; padding: var(--cnt-vp) 0 var(--cnt-vp) var(--cnt-hp); }
#controls-container .mz-fldv_c[hidden] { display: none; }
/* Folder header bar — hidden for sections + the selection editor (css/style.css);
   styled minimally for any folder that does show its header. */
#controls-container .mz-fldv_b { display: block; width: 100%; margin: 0; padding: 0; border: 0; background: transparent; color: inherit; font: inherit; text-align: left; cursor: pointer; }
/* Row spacing: every non-first child gets the unit gap; footer margins are
   intentionally overridden to keep one uniform rhythm. */
#controls-container .mz-fldv_c > *:not(:first-child) { margin-top: var(--cnt-usp); }
/* Labeled row: flex label + fixed-width value cell. */
#controls-container .mz-lblv { display: flex; align-items: center; line-height: 1.3; padding-left: var(--cnt-hp); padding-right: var(--cnt-hp); }
#controls-container .mz-lblv_l { flex: 1; overflow: hidden; padding-left: 4px; padding-right: 16px; -webkit-hyphens: auto; hyphens: auto; }
#controls-container .mz-lblv_v { align-self: flex-start; flex: 0 0 var(--bld-vw); width: var(--bld-vw); }
/* Checkbox — shared by dev-mode toggles and the slice label-cell hijack. */
#controls-container .mz-ckbv_l { display: block; position: relative; }
#controls-container .mz-ckbv_i { position: absolute; left: 0; top: 0; margin: 0; opacity: 0; }
#controls-container .mz-ckbv_w { display: block; position: relative; width: var(--cnt-usz); height: var(--cnt-usz); background-color: var(--in-bg); border-radius: var(--bld-br); cursor: pointer; }
#controls-container .mz-ckbv_i:hover + .mz-ckbv_w { background-color: var(--in-bg-h); }
#controls-container .mz-ckbv_w svg { display: block; position: absolute; inset: 0; margin: auto; width: 16px; height: 16px; opacity: 0; }
#controls-container .mz-ckbv_i:checked + .mz-ckbv_w svg { opacity: 1; }
#controls-container .mz-ckbv_w svg path { fill: none; stroke: var(--in-fg); stroke-width: 2; }
/* Select trigger — the popover itself reuses the .topbar-popover styling. */
#controls-container .mz-lstv_s { display: block; width: 100%; height: var(--cnt-usz); line-height: var(--cnt-usz); margin: 0; padding: 0 20px 0 var(--cnt-hp); border: 0; border-radius: var(--bld-br); background-color: var(--btn-bg); color: var(--btn-fg); font: inherit; font-weight: bold; text-align: left; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; cursor: pointer; -webkit-appearance: none; appearance: none; }
#controls-container .mz-lstv_s:hover { background-color: var(--btn-bg-h); }
#controls-container .mz-lstv_s:disabled { opacity: 0.5; }
/* Text input (selection labels). Background + border come from css/style.css. */
#controls-container .mz-txtv { box-sizing: border-box; width: 100%; height: var(--cnt-usz); line-height: var(--cnt-usz); margin: 0; padding: 0 var(--cnt-hp); border: 1px solid transparent; border-radius: var(--bld-br); color: var(--in-fg); font: inherit; text-align: right; -webkit-appearance: none; appearance: none; }
/* Button (the reductions "no data yet" placeholder). */
#controls-container .mz-btnv_b { display: block; width: 100%; height: var(--cnt-usz); line-height: var(--cnt-usz); border-radius: var(--bld-br); font: inherit; cursor: pointer; }
#controls-container .mz-btnv_b:disabled { opacity: 0.5; cursor: default; }
#controls-container .mz-btnv_t { text-align: center; }
/* Separator (the 1px hairline override lives in css/style.css). */
#controls-container .mz-sprv_r { display: block; width: 100%; height: 2px; margin: 0; border: 0; background-color: var(--grv-fg); }
/* Selection editor: squeeze the single-letter label on the box X/Y/Z interval
   rows so the dual-grip slider gets the full row width. Targets interval-mode
   RangeControls only, so the sphere editor's wider labels (Center X, Radius)
   keep their default column — mirrors the prior ':has(.mz-rslv)' rule. */
#controls-container .mz-fldv[data-role="selection-editor"] .mz-lblv:has(.mv-slider[data-mode="interval"]) .mz-lblv_l { flex: 0 0 18px; padding-left: 0; padding-right: 6px; text-align: center; font-weight: 600; }
#controls-container .mz-fldv[data-role="selection-editor"] .mz-lblv:has(.mv-slider[data-mode="interval"]) .mz-lblv_v { flex: 1 1 auto; width: auto; }
.region-title, .region-label, .region-value { color: var(--magviz-text) !important; }
`;
